import { defineComponent, ref, computed, h } from 'vue'
import icon from './icons'
import { firstDocumentName } from './first_document_name'
import { ROLE_DEFAULTS, defaultRoleName } from './signer_roles'
import { C, s, modalHeader, section, fieldLabel, dropzoneShell, uploadSplitButton, menuItem, signerRoleSelect, applyRoleDefaults, deleteRecipientButton, messageTemplateControls } from './creator_modal_ui'
import { loadMessageTemplates, saveMessageTemplate } from './message_templates'

// A blank role row at position `index`, prefilled from the signing role that sits there.
function blankRecipient (index) {
  const roleName = defaultRoleName(index)
  const defaults = ROLE_DEFAULTS[roleName] || {}

  return {
    id: Date.now() + index,
    roleName,
    name: defaults.default_name || '',
    email: defaults.email || '',
    role: 'signer'
  }
}

export default defineComponent({
  name: 'TemplateCreator',
  props: { onClose: Function },
  setup(props) {
    const docs = ref([])
    const recipients = ref([blankRecipient(0)])
    // Sequential signing is the default: recipients sign one at a time, and each is only
    // invited once the one before them is done. Untick for everyone at once.
    const signingOrderEnabled = ref(true)
    const message = ref({ subject: '', body: '' })

    // Saved email wording (MessageTemplatesController). Fetched once when the modal mounts; a
    // failure leaves the list empty and the picker says so, because this is a convenience beside
    // the subject/body inputs and must not block the form it sits in.
    const messageTemplates = ref([])
    const messageSaveState = ref({ open: false, name: '', saving: false, error: '' })

    loadMessageTemplates().then(list => { messageTemplates.value = list })

    async function saveCurrentMessage () {
      const state = messageSaveState.value

      state.saving = true
      state.error = ''

      try {
        const saved = await saveMessageTemplate({
          name: state.name.trim(),
          subject: message.value.subject || '',
          body: message.value.body || ''
        })

        messageTemplates.value = [...messageTemplates.value, saved]
          .sort((a, b) => a.name.localeCompare(b.name))
        state.open = false
        state.name = ''
      } catch (e) {
        state.error = e.message
      } finally {
        state.saving = false
      }
    }
    const templateName = ref('')
    const templateDescription = ref('')
    const folderName = ref('Default')
    const templateCategory = ref('')
    const fileInput = ref(null)
    const submitting = ref(false)
    // Always use existing token if present (back navigation), otherwise create new
    const existingToken = sessionStorage.getItem('template_creation_token')
    const creationToken = ref(existingToken || Math.random().toString(36).substring(2))
    sessionStorage.setItem('template_creation_token', creationToken.value)

    // Reset submitting state when user navigates back to this page
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') submitting.value = false
      })
      window.addEventListener('pageshow', () => { submitting.value = false })
    }
    const showUploadDropdown = ref(false)
    const showTemplatePicker = ref(false)
    const allTemplates = ref([])
    const filteredTemplates = ref([])
    const templateSearch = ref('')
    const selectedExistingTemplate = ref([])
    const selectionOrder = ref([])
    const loadingTemplates = ref(false)
    // Explicit role mapping (Option 2): final recipients + which source signer maps to which.
    const finalRecipients = ref([])   // [{ key, name, email, role }]
    const roleAssignments = ref({})   // { sourceSubmitterUuid: recipientKey }

    // Advanced Options — shown as full-screen overlay
    const showAdvanced = ref(false)
    const advActiveSection = ref('recipient-privileges')

    // Advanced Options state
    const advCanSignOnPaper = ref(false)
    const advCanDelegate = ref(true)
    const advReminderEnabled = ref(false)
    const advReminderDays = ref(3)
    const advReminderFrequency = ref(3)
    const advExpirationDays = ref(120)
    const advExpirationCustom = ref(120)
    const advExpirationAlertDays = ref(0)
    const advPasswordEnabled = ref(false)
    const advPassword = ref('')
    const advAllowRecipientsEdit = ref(true)
    const advAllowMessageEdit = ref(true)

    const advSections = [
      { id: 'recipient-privileges', label: 'Recipient Privileges' },
      { id: 'reminders', label: 'Reminders' },
      { id: 'expiration', label: 'Expiration' },
      { id: 'template-password', label: 'Template Password' },
      { id: 'template-modification', label: 'Template Modification' },
    ]

    function scrollToSection(id) {
      advActiveSection.value = id
      const el = document.getElementById('adv-section-' + id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    // Prefill from whichever document was added first. Uploading used to fill nothing, so the
    // required name field stayed empty and "Next: Edit Template" only produced an alert. Blank
    // fields only, so anything typed by hand still wins.
    function autofillNameAndSubject() {
      const docName = firstDocumentName(selectionOrder.value, docs.value, selectedExistingTemplate.value)

      if (!docName) return

      if (!templateName.value.trim()) templateName.value = docName
      if (!message.value.subject.trim()) message.value.subject = `Please Complete DocuSeal – ${docName}`
    }

    function addFiles(fileList) {
      Array.from(fileList).forEach((f, i) => {
        const docId = Date.now() + i
        docs.value.push({ id: docId, name: f.name, file: f })
        selectionOrder.value.push({ type: 'file', id: docId })
      })
      autofillNameAndSubject()
      showUploadDropdown.value = false
    }

    function addRecipient() {
      recipients.value.push(blankRecipient(recipients.value.length))
    }
    // Every row carries a delete control, the only row included; deleting that last one clears
    // it back to a fresh default rather than leaving a table with nothing but a header.
    function removeRecipient(id) {
      const remaining = recipients.value.filter(r => r.id !== id)

      recipients.value = remaining.length ? remaining : [blankRecipient(0)]
    }
    function removeDoc(id) {
      docs.value = docs.value.filter(d => d.id !== id)
      selectionOrder.value = selectionOrder.value.filter(s => !(s.type === 'file' && s.id === id))
    }

    const dragIndex = ref(null)

    function onDragStart(index) { dragIndex.value = index }

    // Both recipient lists reorder the same way; only the list being dragged differs.
    function reorder(listRef, e, index) {
      e.preventDefault()
      if (dragIndex.value === null || dragIndex.value === index) return
      const list = [...listRef.value]
      const dragged = list.splice(dragIndex.value, 1)[0]
      list.splice(index, 0, dragged)
      listRef.value = list
      dragIndex.value = index
    }

    function onDragOver(e, index) { reorder(recipients, e, index) }

    function onRoleDragOver(e, index) { reorder(finalRecipients, e, index) }

    function onDragEnd() {
      if (dragIndex.value !== null) {
        renumber(selectedExistingTemplate.value.length > 0 ? finalRecipients : recipients)
      }

      dragIndex.value = null
    }

    // The Order column is a routing NUMBER, the same one DocuSign uses: a lower number signs
    // first, and two rows sharing a number sign at the same time. It is typed, not only dragged
    // -- dragging cannot express "these two together", which is the whole reason the column is
    // editable. A drag still works and renumbers the list 1..n so the numbers match the rows.
    function renumber (listRef) {
      listRef.value.forEach((r, i) => { r.order = i + 1 })
    }

    // Clamped to 1..rows: a number above the row count sorts the same as the last row while
    // reading as an order that does not exist, and 0 collides with "no order set" server-side.
    function setOrder (listRef, row, value) {
      const parsed = parseInt(value, 10)

      row.order = Math.min(Math.max(Number.isNaN(parsed) ? 1 : parsed, 1), listRef.value.length)
    }

    // The Order cell for one row of either recipient table.
    function orderCell (listRef, row, index) {
      if (!signingOrderEnabled.value) {
        return h('div', { style: `width:28px;height:28px;border-radius:6px;background:${C.primarySoft};color:${C.muted};font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center` }, '–')
      }

      return h('input', {
        type: 'number',
        min: 1,
        max: listRef.value.length,
        value: row.order || (index + 1),
        title: 'Signing order. Give two recipients the same number to have them sign at the same time.',
        onChange: e => setOrder(listRef, row, e.target.value),
        style: `width:38px;height:28px;box-sizing:border-box;padding:0 4px;border:1px solid ${C.border};border-radius:6px;background:${C.primarySoft};color:${C.primary};font-size:12px;font-weight:700;text-align:center;font-family:inherit`
      })
    }

    async function openTemplatePicker() {
      showUploadDropdown.value = false
      showTemplatePicker.value = true
      loadingTemplates.value = true
      try {
        const resp = await fetch('/templates/list_all', { headers: { 'Accept': 'application/json' } })
        if (resp.ok) {
          const list = await resp.json()
          allTemplates.value = Array.isArray(list) ? list : []
          filteredTemplates.value = [...allTemplates.value]
        }
      } catch (e) {}
      finally { loadingTemplates.value = false }
    }

    function filterTemplates() {
      const q = templateSearch.value.toLowerCase()
      filteredTemplates.value = allTemplates.value.filter(t => t.name.toLowerCase().includes(q))
    }

    // Selected templates in the order they were picked (matches the server-side merge order).
    function orderedSelectedTemplates() {
      const byId = {}
      selectedExistingTemplate.value.forEach(t => { byId[t.id] = t })
      const ordered = selectionOrder.value
        .filter(s => s.type === 'template' && byId[s.id])
        .map(s => byId[s.id])
      selectedExistingTemplate.value.forEach(t => { if (!ordered.includes(t)) ordered.push(t) })
      return ordered
    }

    const normRole = (str) => (str || '').trim().toLowerCase()
    const newRecipientKey = () => 'r-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)

    // Flatten the picked templates' signers into source roles, in pick order.
    function sourceRoles() {
      const roles = []
      orderedSelectedTemplates().forEach(t => {
        (t.submitters || []).forEach(sub => {
          if (sub && sub.uuid) roles.push({ uuid: sub.uuid, roleName: sub.name || 'Signer', templateName: t.name, defaultName: sub.default_name, defaultEmail: sub.email })
        })
      })
      return roles
    }

    function pruneRecipients() {
      const used = new Set(Object.values(roleAssignments.value))
      finalRecipients.value = finalRecipients.value.filter(r => used.has(r.key) || r.manual)
    }

    // Default mapping: group source roles by (normalized) name — one recipient per distinct
    // role. Same name across templates merges; distinct names stay separate. Preserves any
    // assignment or recipient edit the user already made.
    function rebuildRoleMapping() {
      const roles = sourceRoles()
      if (roles.length === 0) { finalRecipients.value = []; roleAssignments.value = {}; return }
      const nameToKey = {}
      finalRecipients.value.forEach(r => { nameToKey[normRole(r.roleName)] = r.key })
      const assignments = {}
      roles.forEach(role => {
        let key = roleAssignments.value[role.uuid]
        if (!key || !finalRecipients.value.find(r => r.key === key)) {
          key = nameToKey[normRole(role.roleName)]
          if (!key) {
            key = newRecipientKey()
            nameToKey[normRole(role.roleName)] = key
            // A template stores roles; name/email are optional prefills carried from the source.
            finalRecipients.value.push({ key, roleName: role.roleName, name: role.defaultName || '', email: role.defaultEmail || '', role: 'signer' })
          }
        }
        assignments[role.uuid] = key
      })
      roleAssignments.value = assignments
      pruneRecipients()
    }

    // User points a source role at a different recipient (or creates a new one).
    function assignRole(uuid, key) {
      if (key === '__new__') {
        const role = sourceRoles().find(r => r.uuid === uuid)
        key = newRecipientKey()
        finalRecipients.value.push({ key, roleName: role ? role.roleName : 'Recipient', name: '', email: '', role: 'signer' })
      }
      roleAssignments.value = { ...roleAssignments.value, [uuid]: key }
      pruneRecipients()
    }

    // The role list replaces the plain recipient list once a template is picked, so it needs
    // its own add/remove. Manual entries survive pruneRecipients: no source role points at
    // them, which is exactly what prune uses to decide a recipient is dead.
    function addFinalRecipient() {
      finalRecipients.value.push({ ...blankRecipient(finalRecipients.value.length), key: newRecipientKey(), manual: true })
    }

    // Every role is deletable, mapped source roles included. Being mapped used to make a role
    // permanent (dropping it would leave its fields pointing at nothing), which meant the row
    // the sender most wanted to remove was the one row with no delete control. Instead of
    // blocking, the mapping moves to the first role left so the fields follow something real.
    function removeFinalRecipient(key) {
      const remaining = finalRecipients.value.filter(r => r.key !== key)

      if (remaining.length === 0) {
        finalRecipients.value = []
        roleAssignments.value = {}
        rebuildRoleMapping()

        return
      }

      const fallback = remaining[0].key
      const reassigned = { ...roleAssignments.value }

      Object.keys(reassigned).forEach(uuid => {
        if (reassigned[uuid] === key) reassigned[uuid] = fallback
      })

      finalRecipients.value = remaining
      roleAssignments.value = reassigned
    }

    // Role mapping is hidden for now: the default name-based mapping still runs and is
    // still posted as role_map, so fields land on the right signer — only the UI for
    // overriding that mapping is suppressed. Flip to true to bring it back.
    const SHOW_ROLE_MAPPING = false

    // Role-mapping UI, shown in place of the plain recipient list when templates are merged.
    function renderRoleMapping() {
      const cols = '40px 1.1fr 1.1fr 1.3fr 120px 36px'
      return h('div', { style: 'display:flex;flex-direction:column;gap:14px' }, [
        SHOW_ROLE_MAPPING && h('div', { style: 'display:flex;flex-direction:column;gap:6px' },
          sourceRoles().map(role => h('div', {
            key: role.uuid,
            style: `display:flex;align-items:center;gap:10px;padding:8px 12px;border:1px solid ${C.border};border-radius:8px;background:#fff`
          }, [
            h('div', { style: 'flex:1;min-width:0' }, [
              h('div', { style: `font-size:13px;font-weight:600;color:${C.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis` }, role.roleName),
              h('div', { style: 'font-size:11px;color:#9CA3AF;white-space:nowrap;overflow:hidden;text-overflow:ellipsis' }, role.templateName)
            ]),
            h('span', { style: `color:${C.muted};flex-shrink:0` }, '→'),
            h('select', {
              value: roleAssignments.value[role.uuid] || '',
              onChange: e => assignRole(role.uuid, e.target.value),
              style: s.input + ';width:auto;min-width:150px;cursor:pointer;flex-shrink:0'
            }, [
              ...finalRecipients.value.map(r => h('option', { value: r.key }, r.roleName || 'Recipient')),
              h('option', { value: '__new__' }, '+ New recipient')
            ])
          ]))
        ),
        h('label', { style: 'display:flex;align-items:flex-start;gap:8px;cursor:pointer;width:fit-content' }, [
          h('input', { type: 'checkbox', checked: signingOrderEnabled.value, onChange: e => { signingOrderEnabled.value = e.target.checked }, style: `width:15px;height:15px;margin-top:2px;cursor:pointer;accent-color:${C.primary}` }),
          h('div', null, [
            h('div', { style: `font-size:13px;font-weight:600;color:${C.text}` }, 'Set signing order'),
            h('div', { style: `font-size:12px;color:${C.muted};margin-top:2px` }, 'Recipients will be asked to sign in the order listed below.')
          ])
        ]),
        h('div', { style: s.tableWrap }, [
          h('div', { style: s.tableHead + `;grid-template-columns:${cols}` }, [
            h('span', null, 'Order'), h('span', null, 'Role'), h('span', null, 'Name (optional)'),
            h('span', null, 'Email (optional)'), h('span', null, 'Action'), h('span', null, '')
          ]),
          ...finalRecipients.value.map((r, i) => h('div', {
            key: r.key,
            draggable: signingOrderEnabled.value,
            onDragstart: () => signingOrderEnabled.value && onDragStart(i),
            onDragover: e => signingOrderEnabled.value && onRoleDragOver(e, i),
            onDragend: onDragEnd,
            style: s.tableRow + `;grid-template-columns:${cols};${i === finalRecipients.value.length - 1 ? 'border-bottom:none' : ''}`
          }, [
            orderCell(finalRecipients, r, i),
            signerRoleSelect(r.roleName, name => { r.roleName = name; applyRoleDefaults(r, name) }),
            h('input', { value: r.name, placeholder: 'Full name', onInput: e => r.name = e.target.value, style: s.input }),
            h('input', { value: r.email, placeholder: 'email@example.com', type: 'email', onInput: e => r.email = e.target.value, style: s.input }),
            h('select', { value: r.role, onChange: e => r.role = e.target.value, style: s.input + ';cursor:pointer' }, [
              h('option', { value: 'signer' }, 'Needs to Sign'),
              h('option', { value: 'cc' }, 'Receives a Copy'),
              h('option', { value: 'viewer' }, 'Needs to View')
            ]),
            deleteRecipientButton(() => removeFinalRecipient(r.key), 'Delete role')
          ]))
        ]),
        h('button', { onClick: addFinalRecipient, style: s.addLink }, [icon('plus', 14), ' Add Role'])
      ])
    }

    // Multi-select: toggle a template in/out of the selection, keeping the picker open.
    // Order added = merge hierarchy; the selected list below can then be reordered by drag.
    function toggleTemplateSelection(t) {
      if (selectedExistingTemplate.value.find(x => x.id === t.id)) {
        selectedExistingTemplate.value = selectedExistingTemplate.value.filter(x => x.id !== t.id)
        selectionOrder.value = selectionOrder.value.filter(s => !(s.type === 'template' && s.id === t.id))
      } else {
        selectedExistingTemplate.value = [...selectedExistingTemplate.value, t]
        selectionOrder.value.push({ type: 'template', id: t.id })
      }
      // The server appends "_<uuid>" and names by the first PDF regardless, so this only has to
      // get the blank-field case right.
      if (selectedExistingTemplate.value.length) autofillNameAndSubject()

      rebuildRoleMapping()
    }

    // Drag-reorder the selected templates. Keeps selectionOrder's template entries in sync
    // (file entries stay in place) and refreshes recipient labels once the drop settles.
    const tplDragIndex = ref(null)
    function syncTemplateSelectionOrder() {
      const ids = selectedExistingTemplate.value.map(t => t.id)
      let ti = 0
      selectionOrder.value = selectionOrder.value.map(s =>
        s.type === 'template' ? { type: 'template', id: ids[ti++] } : s)
    }
    function onTplDragStart(i) { tplDragIndex.value = i }
    function onTplDragOver(e, i) {
      e.preventDefault()
      if (tplDragIndex.value === null || tplDragIndex.value === i) return
      const list = [...selectedExistingTemplate.value]
      const [moved] = list.splice(tplDragIndex.value, 1)
      list.splice(i, 0, moved)
      selectedExistingTemplate.value = list
      syncTemplateSelectionOrder()
      tplDragIndex.value = i
    }
    function onTplDragEnd() { tplDragIndex.value = null; rebuildRoleMapping() }

    function proceedToEditor() {
      submitting.value = true

      sessionStorage.setItem('template_recipients', JSON.stringify(recipients.value))
      sessionStorage.setItem('template_message', JSON.stringify(message.value))

      const form = document.createElement('form')
      form.method = 'POST'
      form.action = '/templates'
      form.enctype = 'multipart/form-data'
      form.style.display = 'none'

      function addHidden(name, value) {
        const i = document.createElement('input')
        i.type = 'hidden'; i.name = name; i.value = value
        form.appendChild(i)
      }

      addHidden('authenticity_token', document.querySelector('meta[name=csrf-token]')?.content || '')
      addHidden('creation_token', creationToken.value)
      if (selectedExistingTemplate.value.length === 1 && docs.value.length === 0) {
        // Single existing template, no uploads: use the server's clone path
        addHidden('base_template_id', selectedExistingTemplate.value[0].id)
      } else if (selectedExistingTemplate.value.length > 0) {
        // Multiple templates and/or mixed with uploads: merge server-side
        addHidden('selection_order', JSON.stringify(selectionOrder.value))
        selectedExistingTemplate.value.forEach(t => addHidden('existing_template_ids[]', t.id))
      }
      addHidden('template[name]', templateName.value.trim() || 'Untitled Template')
      addHidden('template_description', templateDescription.value)
      addHidden('folder_name', templateCategory.value || folderName.value || 'Default')
      addHidden('template_category', templateCategory.value || '')
      addHidden('message_subject', message.value.subject)
      addHidden('message_body', message.value.body)
      addHidden('set_signing_order', signingOrderEnabled.value ? '1' : '0')

      // Advanced Options
      addHidden('adv_can_sign_on_paper',  advCanSignOnPaper.value  ? '1' : '0')
      addHidden('adv_can_delegate',       advCanDelegate.value     ? '1' : '0')
      addHidden('adv_reminder_enabled',   advReminderEnabled.value ? '1' : '0')
      addHidden('adv_reminder_days',      advReminderDays.value)
      addHidden('adv_reminder_frequency', advReminderFrequency.value)
      addHidden('adv_expiration_days',    advExpirationCustom.value)
      addHidden('adv_expiration_alert_days', advExpirationAlertDays.value)
      addHidden('adv_password_enabled',   advPasswordEnabled.value ? '1' : '0')
      addHidden('adv_password',           advPasswordEnabled.value ? advPassword.value : '')
      addHidden('adv_allow_recipients_edit', advAllowRecipientsEdit.value ? '1' : '0')
      addHidden('adv_allow_message_edit',    advAllowMessageEdit.value    ? '1' : '0')

      if (selectedExistingTemplate.value.length > 0) {
        // Explicit role mapping: send final recipients + source-signer → recipient map.
        addHidden('final_recipients', JSON.stringify(finalRecipients.value.map((r, i) => ({ key: r.key, role_name: r.roleName, name: r.name, email: r.email, signing_role: r.role, signing_order: r.order || i + 1 }))))
        addHidden('role_map', JSON.stringify(roleAssignments.value))
      } else {
        recipients.value.forEach((r, i) => {
          if (r.roleName || r.name || r.email) {
            [['role', r.roleName || `Signer ${i+1}`],['name', r.name],['email', r.email],['signing_role', r.role], ['signing_order', r.order || i + 1]].forEach(([key, val]) => {
              addHidden(`recipients[${i}][${key}]`, val || '')
            })
          }
        })
      }

      if (docs.value.length > 0) {
        const fileTransfer = new DataTransfer()
        docs.value.forEach(d => fileTransfer.items.add(d.file))
        const fileField = document.createElement('input')
        fileField.type = 'file'; fileField.name = 'files[]'; fileField.multiple = true
        fileField.files = fileTransfer.files
        form.appendChild(fileField)
      }

      document.body.appendChild(form)
      form.submit()
    }

    // ── DocuSign-style toggle ──────────────────────────────────────────
    function dsToggle(checked, onChange) {
      return h('div', {
        onClick: () => onChange({ target: { checked: !checked } }),
        style: `position:relative;display:inline-flex;align-items:center;width:44px;height:24px;border-radius:12px;cursor:pointer;transition:.2s;background:${checked ? C.primary : '#D1D5DB'};flex-shrink:0`
      }, [
        h('span', {
          style: `position:absolute;width:18px;height:18px;border-radius:50%;background:#fff;transition:.2s;left:${checked ? '23px' : '3px'};box-shadow:0 1px 3px rgba(0,0,0,.3)`
        })
      ])
    }

    // ── DocuSign-style checkbox ────────────────────────────────────────
    function dsCheckbox(checked, onChange, label, sublabel) {
      return h('label', { style: 'display:flex;align-items:flex-start;gap:10px;cursor:pointer;margin-bottom:12px' }, [
        h('input', {
          type: 'checkbox', checked,
          onChange,
          style: `width:16px;height:16px;margin-top:2px;accent-color:${C.primary};flex-shrink:0`
        }),
        h('div', null, [
          h('div', { style: `font-size:14px;color:${C.text}` }, label),
          sublabel && h('div', { style: `font-size:12px;color:${C.muted};margin-top:1px` }, sublabel)
        ])
      ])
    }

    // ── Advanced Options full-screen panel ────────────────────────────
    function renderAdvancedPanel() {
      const sectionStyle = 'padding:32px 40px;border-bottom:1px solid #E5E7EB'
      const titleStyle = 'font-size:20px;font-weight:400;color:#1a1a2e;margin:0 0 6px'
      const subtitleStyle = 'font-size:14px;color:#4B5563;margin:0 0 20px'
      const fieldLabelStyle = 'font-size:13px;color:#374151;margin-bottom:4px;font-weight:500'

      // Compute expiry date preview
      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + advExpirationCustom.value)
      const expiryStr = expiryDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })

      return h('div', {
        style: 'position:fixed;inset:0;z-index:10000;background:#fff;display:flex;flex-direction:column'
      }, [

        // ── Top bar ──────────────────────────────────────────────────
        h('div', { style: 'display:flex;align-items:center;justify-content:space-between;padding:0 24px;height:56px;border-bottom:1px solid #E5E7EB;flex-shrink:0' }, [
          h('h1', { style: 'font-size:20px;font-weight:400;color:#1a1a2e;margin:0' }, 'Advanced Options'),
          h('button', {
            onClick: () => { showAdvanced.value = false },
            style: 'background:none;border:none;cursor:pointer;color:#6B7280;font-size:24px;line-height:1;padding:4px'
          }, icon('x', 14))
        ]),

        // ── Body: sidebar + content ──────────────────────────────────
        h('div', { style: 'display:flex;flex:1;overflow:hidden' }, [

          // Left sidebar nav
          h('nav', { style: 'width:200px;border-right:1px solid #E5E7EB;padding:16px 0;flex-shrink:0;overflow-y:auto' },
            advSections.map(sec =>
              h('div', {
                key: sec.id,
                onClick: () => scrollToSection(sec.id),
                style: `padding:10px 20px;font-size:14px;cursor:pointer;color:${advActiveSection.value === sec.id ? C.primary : '#374151'};font-weight:${advActiveSection.value === sec.id ? '600' : '400'};border-left:${advActiveSection.value === sec.id ? '3px solid ' + C.primary : '3px solid transparent'}`
              }, sec.label)
            )
          ),

          // Right scrollable content
          h('div', {
            style: 'flex:1;overflow-y:auto',
            onScroll: (e) => {
              // Update active section based on scroll position
              const container = e.target
              for (const sec of [...advSections].reverse()) {
                const el = document.getElementById('adv-section-' + sec.id)
                if (el && el.offsetTop <= container.scrollTop + 80) {
                  advActiveSection.value = sec.id
                  break
                }
              }
            }
          }, [

            // ── Recipient Privileges ─────────────────────────────
            h('div', { id: 'adv-section-recipient-privileges', style: sectionStyle }, [
              h('div', { style: 'display:flex;align-items:flex-start;justify-content:space-between' }, [
                h('div', null, [
                  h('h2', { style: titleStyle }, 'Recipient Privileges'),
                  h('p', { style: subtitleStyle }, 'Give recipients options for how they sign.'),
                ]),
                h('span', { style: 'color:#9CA3AF;cursor:default;display:flex' }, icon('lock', 18))
              ]),
              dsCheckbox(
                advCanDelegate.value,
                e => { advCanDelegate.value = e.target.checked },
                'Recipients can change signing responsibility or assign a delegate',
                null
              ),
            ]),

            // ── Reminders ────────────────────────────────────────
            h('div', { id: 'adv-section-reminders', style: sectionStyle }, [
              h('h2', { style: titleStyle }, 'Reminders'),
              h('p', { style: subtitleStyle }, 'Follow up with automatic reminders. Signers will receive emails until they sign or decline the envelope.'),
              h('div', { style: 'display:flex;align-items:center;gap:10px;margin-bottom:20px' }, [
                dsToggle(advReminderEnabled.value, e => { advReminderEnabled.value = e.target.checked }),
                h('span', { style: 'font-size:14px;color:#374151' }, 'Turn on auto reminders')
              ]),
              advReminderEnabled.value && h('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:8px' }, [
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, 'Send first reminder'),
                  h('div', { style: 'display:flex;gap:10px;align-items:center' }, [
                    h('select', {
                      value: advReminderDays.value,
                      onChange: e => { advReminderDays.value = parseInt(e.target.value) },
                      style: s.input + ';background:#fff;cursor:pointer'
                    }, [1,2,3,5,7,10,14].map(d => h('option', { value: d }, `${d} day${d>1?'s':''} after sending`))),
                  ])
                ]),
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, 'Send reminder every'),
                  h('select', {
                    value: advReminderFrequency.value,
                    onChange: e => { advReminderFrequency.value = parseInt(e.target.value) },
                    style: s.input + ';background:#fff;cursor:pointer'
                  }, [1,2,3,5,7].map(d => h('option', { value: d }, `${d} day${d>1?'s':''}`)))
                ])
              ])
            ]),

            // ── Expiration ───────────────────────────────────────
            h('div', { id: 'adv-section-expiration', style: sectionStyle }, [
              h('h2', { style: titleStyle }, 'Expiration'),
              h('p', { style: subtitleStyle }, `By default, envelopes expire after 120 days. Recipients can no longer view or sign an envelope after it expires.`),
              h('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:end' }, [
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, 'Days until envelope expires'),
                  h('select', {
                    value: [7,14,30,60,90,120,180,365].includes(advExpirationCustom.value) ? advExpirationCustom.value : 'custom',
                    onChange: e => {
                      if (e.target.value !== 'custom') advExpirationCustom.value = parseInt(e.target.value)
                    },
                    style: s.input + ';background:#fff;cursor:pointer'
                  }, [
                    h('option', { value: 7 }, '7 days'),
                    h('option', { value: 14 }, '14 days'),
                    h('option', { value: 30 }, '30 days'),
                    h('option', { value: 60 }, '60 days'),
                    h('option', { value: 90 }, '90 days'),
                    h('option', { value: 120 }, '120 days'),
                    h('option', { value: 180 }, '180 days'),
                    h('option', { value: 365 }, '1 year'),
                    h('option', { value: 'custom' }, 'Custom days'),
                  ])
                ]),
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, [
                    'Custom number of days ',
                    h('span', { style: 'color:#DC2626' }, '*')
                  ]),
                  h('input', {
                    type: 'number', min: 1, max: 999,
                    value: advExpirationCustom.value,
                    onInput: e => { advExpirationCustom.value = Math.max(1, parseInt(e.target.value) || 1) },
                    style: s.input
                  })
                ])
              ]),
              h('div', { style: 'margin-top:8px;font-size:13px;color:#374151' }, [
                'Envelopes will expire on: ',
                h('strong', null, expiryStr)
              ]),
              h('div', { style: 'display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:end;margin-top:20px' }, [
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, 'Send alert'),
                  h('select', {
                    value: advExpirationAlertDays.value === 0 ? 'custom' : advExpirationAlertDays.value,
                    onChange: e => {
                      if (e.target.value !== 'custom') advExpirationAlertDays.value = parseInt(e.target.value)
                      else advExpirationAlertDays.value = 0
                    },
                    style: s.input + ';background:#fff;cursor:pointer'
                  }, [
                    h('option', { value: 3 }, '3 days before'),
                    h('option', { value: 7 }, '7 days before'),
                    h('option', { value: 14 }, '14 days before'),
                    h('option', { value: 'custom' }, 'Custom days'),
                  ])
                ]),
                h('div', null, [
                  h('div', { style: fieldLabelStyle }, [
                    'Custom number of days ',
                    h('span', { style: 'color:#DC2626' }, '*')
                  ]),
                  h('input', {
                    type: 'number', min: 0, max: 999,
                    value: advExpirationAlertDays.value,
                    onInput: e => { advExpirationAlertDays.value = Math.max(0, parseInt(e.target.value) || 0) },
                    style: s.input
                  })
                ])
              ])
            ]),

            // ── Template Password ────────────────────────────────
            h('div', { id: 'adv-section-template-password', style: sectionStyle }, [
              h('h2', { style: titleStyle }, 'Template Password'),
              h('p', { style: subtitleStyle }, 'Use a password to protect this template from being edited.'),
              h('div', { style: 'display:flex;align-items:center;gap:10px;margin-bottom:16px' }, [
                dsToggle(advPasswordEnabled.value, e => { advPasswordEnabled.value = e.target.checked }),
                h('span', { style: 'font-size:14px;color:#374151' }, 'Turn on password protection')
              ]),
              advPasswordEnabled.value && h('div', { style: 'max-width:320px' }, [
                h('div', { style: fieldLabelStyle }, 'Password'),
                h('input', {
                  type: 'password',
                  value: advPassword.value,
                  placeholder: 'Enter password',
                  onInput: e => { advPassword.value = e.target.value },
                  style: s.input
                })
              ])
            ]),

            // ── Template Modification ────────────────────────────
            h('div', { id: 'adv-section-template-modification', style: sectionStyle }, [
              h('h2', { style: titleStyle }, 'Template Modification'),
              h('p', { style: subtitleStyle }, 'Limit the changes someone can make when they use this template.'),
              dsCheckbox(
                advAllowRecipientsEdit.value,
                e => { advAllowRecipientsEdit.value = e.target.checked },
                'Allow senders to edit, add, or remove recipients',
                null
              ),
              dsCheckbox(
                advAllowMessageEdit.value,
                e => { advAllowMessageEdit.value = e.target.checked },
                'Allow senders to edit the subject, email, or private messages',
                null
              ),
            ]),

          ])
        ]),

        // ── Bottom Save bar ──────────────────────────────────────────
        h('div', { style: 'border-top:1px solid #E5E7EB;padding:14px 24px;display:flex;justify-content:flex-end;flex-shrink:0;background:#fff' }, [
          h('button', {
            onClick: () => { showAdvanced.value = false },
            style: `padding:10px 32px;background:${C.primary};color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit`
          }, 'Save')
        ])
      ])
    }

    const CATEGORIES = ['Agency Agreements','Bank Account Opening Agreements','Confidentiality Agreements','Consulting Agreements','Contractor Agreements','Credit Card Agreements','Distribution Agreements','Employment Contracts','Franchise Agreements','Independent Contractor Agreements','Intellectual Property Assignment Agreements','Investment Account Agreements','Joint Venture Agreements','Lease Agreements','Licensing Agreements','Loan Agreements','Loan Applications','Non-Disclosure Agreements (NDAs)','Onboarding Agreements','Other','Partnership Agreements','Purchase Agreements','Sales Contracts','Service Agreements','Software License Agreements','Vendor Agreements','Wealth Management Agreements']

    function recipientTableHeader (cols) {
      return h('div', { style: s.tableHead + `;grid-template-columns:${cols}` }, [
        h('span', null, 'Order'),
        h('span', null, 'Role'),
        h('span', null, 'Name (optional)'),
        h('span', null, 'Email (optional)'),
        h('span', null, 'Action'),
        h('span', null, '')
      ])
    }

    // ── Main render ───────────────────────────────────────────────────
    return () => h('div', null, [

      // Advanced Options full panel (on top of everything)
      showAdvanced.value && renderAdvancedPanel(),

      // Main modal
      h('div', {
        style: s.overlay,
        onClick: (e) => { if (e.target === e.currentTarget) props.onClose?.() }
      }, [
        h('div', { style: s.modal }, [

          modalHeader({
            iconName: 'file_text',
            title: 'New Document Template',
            subtitle: 'Create a reusable template to save time.',
            onAdvanced: () => { showAdvanced.value = true },
            onClose: props.onClose
          }),

          // Body
          h('div', { style: s.body }, [
            h('div', { style: 'display:flex;flex-direction:column' }, [

              // No template name field: it only ever echoed the first document's name, which
              // autofillNameAndSubject already fills, and being required it blocked the wizard
              // whenever the prefill had not run. The name still travels in template[name].
              section({ icon: 'file_text', title: 'Description', subtitle: 'Optional note about what this template is for.' }, [
                h('div', null, [
                  h('textarea', {
                    value: templateDescription.value,
                    placeholder: 'Description (optional)',
                    rows: 2,
                    onInput: e => { templateDescription.value = e.target.value },
                    style: s.input + ';resize:vertical;min-height:72px'
                  })
                ])
              ]),

              section({ icon: 'folder', title: 'Organize', subtitle: 'Choose a category or folder for this template.' }, [
                h('div', null, [
                  fieldLabel('Category / Folder'),
                  h('select', {
                    value: templateCategory.value,
                    onChange: e => { templateCategory.value = e.target.value },
                    style: s.input + ';cursor:pointer'
                  }, [
                    h('option', { value: '' }, '-- Select --'),
                    ...CATEGORIES.map(cat => h('option', { value: cat }, cat))
                  ])
                ])
              ]),

              section({ icon: 'upload', title: 'Add documents', subtitle: 'Upload a PDF or choose an existing template.' }, [
                dropzoneShell([
                  h('div', { style: `margin-bottom:12px;display:flex;justify-content:center;color:${C.primary}` }, icon('upload', 28)),
                  h('div', { style: `font-size:13px;color:${C.muted};margin-bottom:14px` }, 'Drop your files here or'),
                  uploadSplitButton({
                    onUpload: () => fileInput.value?.click(),
                    onToggle: e => { e.stopPropagation(); showUploadDropdown.value = !showUploadDropdown.value },
                    open: showUploadDropdown.value,
                    menuItems: [
                      menuItem('From Computer', 'folder', () => fileInput.value?.click()),
                      h('div', { style: `height:1px;background:${C.border};margin:0 10px` }),
                      menuItem('Use a Template', 'file_text', openTemplatePicker)
                    ]
                  }),
                  h('input', { ref: fileInput, type: 'file', multiple: true, accept: '.pdf,image/*', style: 'display:none', onChange: e => addFiles(e.target.files) })
                ], { onDrop: e => { e.preventDefault(); addFiles(e.dataTransfer.files) } }),


                docs.value.length > 0 && h('div', { style: 'display:flex;gap:10px;flex-wrap:wrap' },
                  docs.value.map(d => h('div', { key: d.id, style: `width:140px;border:1px solid ${C.border};border-radius:10px;overflow:hidden;background:#fff;position:relative` }, [
                    h('div', { style: `height:80px;background:${C.primarySoft};display:flex;align-items:center;justify-content:center;color:${C.primary}` }, icon('file_text', 28)),
                    h('div', { style: `padding:6px 8px;font-size:11px;font-weight:600;color:${C.text};overflow:hidden;text-overflow:ellipsis;white-space:nowrap` }, d.name),
                    h('button', { onClick: () => removeDoc(d.id), style: 'position:absolute;top:4px;right:4px;width:18px;height:18px;border-radius:4px;border:none;background:rgba(0,0,0,.4);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center' }, icon('x', 14))
                  ]))
                ),

                selectedExistingTemplate.value.length > 0 && h('div', { style: 'display:flex;flex-direction:column;gap:8px' }, [
                  selectedExistingTemplate.value.length > 1 && h('div', { style: `font-size:11px;color:${C.muted}` }, 'Drag to reorder — top to bottom is the document order.'),
                  ...selectedExistingTemplate.value.map((t, i) => h('div', {
                    key: t.id,
                    draggable: true,
                    onDragstart: () => onTplDragStart(i),
                    onDragover: e => onTplDragOver(e, i),
                    onDragend: onTplDragEnd,
                    style: `display:flex;align-items:center;gap:12px;padding:10px 14px;border:1.5px solid ${C.primary};border-radius:10px;background:${C.primarySoft};cursor:move;opacity:${tplDragIndex.value === i ? '.4' : '1'}`
                  }, [
                    h('span', { style: `color:${C.muted};display:flex` }, icon('grip', 16)),
                    h('span', { style: `width:22px;height:22px;border-radius:50%;background:${C.primary};color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0` }, String(i + 1)),
                    h('span', { style: `display:flex;color:${C.primary}` }, icon('file_text', 18)),
                    h('div', { style: `flex:1;font-size:13px;font-weight:600;color:${C.text}` }, t.name),
                    h('button', { onClick: () => toggleTemplateSelection(t), style: s.btnGhost }, [icon('x', 13), ' Remove'])
                  ]))
                ]),

                showTemplatePicker.value && h('div', {
                  style: 'position:fixed;inset:0;background:rgba(15,23,42,.45);z-index:10000;display:flex;align-items:center;justify-content:center',
                  onClick: e => e.target === e.currentTarget && (showTemplatePicker.value = false)
                }, [
                  h('div', { style: `background:#fff;border-radius:14px;width:520px;max-height:65vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 50px rgba(0,0,0,.2)` }, [
                    h('div', { style: `padding:18px 22px;border-bottom:1px solid ${C.border};display:flex;align-items:center;justify-content:space-between` }, [
                      h('div', { style: `font-size:15px;font-weight:700;color:${C.text}` }, 'Choose Templates'),
                      h('button', { onClick: () => showTemplatePicker.value = false, style: 'border:none;background:none;cursor:pointer;color:#6B7280' }, icon('x', 14))
                    ]),
                    h('div', { style: `padding:10px 22px;border-bottom:1px solid ${C.border}` }, [
                      h('input', { value: templateSearch.value, placeholder: 'Search...', onInput: e => { templateSearch.value = e.target.value; filterTemplates() }, style: s.input })
                    ]),
                    h('div', { style: 'flex:1;overflow:auto;padding:12px 22px;display:flex;flex-direction:column;gap:8px' },
                      loadingTemplates.value
                        ? [h('div', { style: `text-align:center;padding:20px;color:${C.muted}` }, 'Loading...')]
                        : filteredTemplates.value.map(t => {
                            const selIdx = selectedExistingTemplate.value.findIndex(x => x.id === t.id)
                            const isSel = selIdx !== -1
                            return h('div', {
                              key: t.id, onClick: () => toggleTemplateSelection(t),
                              style: `display:flex;align-items:center;gap:10px;padding:10px 14px;border:${isSel ? '1.5px solid ' + C.primary : '1px solid ' + C.border};border-radius:10px;cursor:pointer;background:${isSel ? C.primarySoft : '#fff'}`
                            }, [
                              h('div', { style: `width:32px;height:36px;background:${C.primarySoft};border-radius:6px;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:${C.primary}` }, icon('file_text', 16)),
                              h('div', { style: 'flex:1;min-width:0' }, [
                                h('div', { style: `font-size:13px;font-weight:600;color:${C.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis` }, t.name),
                                h('div', { style: 'font-size:11px;color:#9CA3AF;margin-top:2px' }, [t.author, t.created_at ? new Date(t.created_at).toLocaleString() : ''].filter(Boolean).join(' · '))
                              ]),
                              isSel
                                ? h('span', { style: `width:22px;height:22px;border-radius:50%;background:${C.primary};color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0` }, String(selIdx + 1))
                                : h('span', { style: `font-size:12px;color:${C.primary};font-weight:600;flex-shrink:0` }, 'Select')
                            ])
                          })
                    ),
                    h('div', { style: `padding:12px 22px;border-top:1px solid ${C.border};display:flex;align-items:center;justify-content:space-between` }, [
                      h('div', { style: `font-size:12px;color:${C.muted}` }, `${selectedExistingTemplate.value.length} selected`),
                      h('button', { onClick: () => { showTemplatePicker.value = false; templateSearch.value = '' }, style: s.btn }, 'Done')
                    ])
                  ])
                ])
              ]),

              // Recipients
              selectedExistingTemplate.value.length > 0
                ? section({ icon: 'users', title: 'Add recipients', subtitle: 'Define the signing roles this template needs.' }, [renderRoleMapping()])
                : section({ icon: 'users', title: 'Add recipients', subtitle: 'Define the signing roles this template needs.' }, [
                  h('label', { style: 'display:flex;align-items:flex-start;gap:8px;cursor:pointer;width:fit-content' }, [
                    h('input', { type: 'checkbox', checked: signingOrderEnabled.value, onChange: e => { signingOrderEnabled.value = e.target.checked }, style: `width:15px;height:15px;margin-top:2px;accent-color:${C.primary}` }),
                    h('div', null, [
                      h('div', { style: `font-size:13px;font-weight:600;color:${C.text}` }, 'Set signing order'),
                      h('div', { style: `font-size:12px;color:${C.muted};margin-top:2px` }, 'Recipients will be asked to sign in the order listed below.')
                    ])
                  ]),
                  h('div', { style: s.tableWrap }, [
                    recipientTableHeader('40px 1.1fr 1.1fr 1.3fr 120px 36px'),
                    ...recipients.value.map((r, i) => h('div', {
                      key: r.id,
                      draggable: signingOrderEnabled.value,
                      onDragstart: () => signingOrderEnabled.value && onDragStart(i),
                      onDragover: e => signingOrderEnabled.value && onDragOver(e, i),
                      onDragend: onDragEnd,
                      style: s.tableRow + `;grid-template-columns:40px 1.1fr 1.1fr 1.3fr 120px 36px;${i === recipients.value.length - 1 ? 'border-bottom:none' : ''}`
                    }, [
                      orderCell(recipients, r, i),
                      signerRoleSelect(r.roleName, name => { r.roleName = name; applyRoleDefaults(r, name) }),
                      h('input', { value: r.name, placeholder: 'Full name', onInput: e => r.name = e.target.value, style: s.input }),
                      h('input', { value: r.email, placeholder: 'email@example.com', type: 'email', onInput: e => r.email = e.target.value, style: s.input }),
                      h('select', { value: r.role, onChange: e => r.role = e.target.value, style: s.input + ';cursor:pointer' }, [
                        h('option', { value: 'signer' }, 'Needs to Sign'),
                        h('option', { value: 'cc' }, 'Receives a Copy'),
                        h('option', { value: 'viewer' }, 'Needs to View')
                      ]),
                      deleteRecipientButton(() => removeRecipient(r.id), 'Delete role')
                    ]))
                  ]),
                  h('button', { onClick: addRecipient, style: s.addLink }, [icon('plus', 14), ' Add Recipient'])
                ]),

              section({ icon: 'message', title: 'Add message', subtitle: 'Optional email subject and body for invites.' }, [
                messageTemplateControls({
                  templatesRef: messageTemplates,
                  messageRef: message,
                  saveStateRef: messageSaveState,
                  onSave: saveCurrentMessage
                }),
                h('div', null, [
                  fieldLabel('Subject line'),
                  h('input', { value: message.value.subject, placeholder: 'Please Complete DocuSeal – Document', onInput: e => message.value.subject = e.target.value, style: s.input })
                ]),
                h('div', null, [
                  fieldLabel('Message'),
                  h('textarea', { value: message.value.body, rows: 3, placeholder: 'Please review and sign the attached document.', onInput: e => message.value.body = e.target.value, style: s.input + ';resize:vertical' })
                ])
              ])
            ])
          ]),

          h('div', { style: s.footer }, [
            h('button', { onClick: props.onClose, style: s.btnSec }, 'Cancel'),
            h('button', {
              onClick: proceedToEditor,
              disabled: submitting.value,
              style: s.btn + `;opacity:${submitting.value ? 0.5 : 1}`
            }, submitting.value ? 'Creating...' : 'Create Template')
          ])
        ])
      ])
    ])
  }
})